import React, { useState } from "react";
import PropTypes from "prop-types";
import RescheduleChatBtn from "./reschedule-chat-btn";
import JoinChatBtn from "./join-chat-btn";
import moment from "moment";
import UnconfirmedAppointmentActions from "./unconfirmed-appointment-actions";
import ConfirmationModal from "./confirmation-modal";
import { fromTo } from "utils/misc";
import { getCancelAriaLabelledBy } from "./appointment-utils";
import { useAppointmentLogic } from "./use-appointment-logic";
import useTranslate from "hooks/use-translate-language";
import ClockIcon from "assets/images/clock_gray.png";
import { Button } from "components/ui_new/index";
import { AddToCalendar } from "components/add-to-calendar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const UpcomingChatMobileCard = ({ chats, master_chat_type }) => {
  const translate = useTranslate();
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  const {
    displaySpinner,
    speaksSpanish,
    browserTimezone,
    chat_details,
    topic_details,
    is_confirmed,
    is_cancelled,
    getStatusColor,
    remainingTime,
    onCancelAppointment,
    startTimeFromCSTToBrowser,
    endTimeFromCSTToBrowser,
    timeUntilAppointment
  } = useAppointmentLogic(chats, master_chat_type);

  // Calendar event details
  const providerName = chat_details.dme_details?.name || translate("your provider");
  const calendarTitle = `${translate(master_chat_type)} ${translate("with")} ${providerName}`;
  const calendarDescription = chat_details.description || `${translate(master_chat_type)} ${translate("with")} ${providerName} ${translate("in")} My Sleep Coach`;

  return (
    <div className="upcoming-chat-mobile-card">
      <div className="mobile-card-header">
        <div className="mobile-card-left-container">
          <h3 className="sr-only">{translate("date")}</h3>
          <div className="mobile-card-date-time">
            <img src={ClockIcon} alt="" aria-hidden="true" />
            <div className="date-time-content">
              <div style={{ fontWeight: "bold" }}>
                {`${moment(chat_details.date_time).format("DD")}, ${moment(
                  chat_details.date_time
                ).format("MMMM")} ${moment(chat_details.date_time).format(
                  "yyyy"
                )}`}
              </div>
              <div className="time-range">
                {`${fromTo(
                  startTimeFromCSTToBrowser,
                  endTimeFromCSTToBrowser,
                  translate("to")
                )} (${moment.tz(browserTimezone).format("z")})`}
              </div>
            </div>
          </div>
          <h3 className="sr-only">{translate("status")}</h3>
          <div className="status-section">
            <span
              className="status-badge"
              style={getStatusColor(chat_details.meeting_status)}
            >
              {chat_details.meeting_status === "Un-confirmed"
                ? translate("confirmationPending")
                : chat_details.meeting_status.charAt(0).toUpperCase() +
                  chat_details.meeting_status.slice(1)}
            </span>
          </div>
        </div>
        <div className="mobile-card-right-container">
          <h3 className="sr-only">{translate("topic")}</h3>
          <div>
            {`${
              !speaksSpanish
                ? topic_details[0]?.topic_name
                : topic_details[0]?.spanish_topic_name ??
                  topic_details[0]?.topic_name
            } | ${translate(master_chat_type)}`}
          </div>
          <h3 className="sr-only">{translate("with")}</h3>
          <div>
            <div>{translate("with")}</div>
            <div>{chat_details.dme_details?.name}</div>
          </div>
          <h3 className="sr-only">{translate("startsIn")}</h3>
          <div className="countdown-section">
            {!is_cancelled &&
              (timeUntilAppointment > 0 ? (
                <div className="countdown">
                  <div className="countdown-days">{`${
                    remainingTime.days
                  } ${translate("days")}`}</div>
                  <div className="countdown-time">
                    {`${remainingTime.hours} ${translate("hours")} : ${
                      remainingTime.minutes
                    } ${translate("minutes")}`}
                  </div>
                </div>
              ) : (
                <div className="starts-now">{translate("startsNow")}</div>
              ))}
          </div>
        </div>
      </div>

      {chat_details.description && (
        <>
          <h3 className="sr-only">{translate("description")}</h3>
          <div className="mobile-card-description">
            {chat_details.description}
          </div>
        </>
      )}

      <h3 className="sr-only">{translate("actions")}</h3>
      <div className="mobile-card-actions">
        {!is_cancelled &&
          ((remainingTime.days == 0 &&
            remainingTime.hours == 0 &&
            remainingTime.minutes <= 5) ||
            timeUntilAppointment <= 0) && (
            <JoinChatBtn
              {...chat_details}
              master_chat_type={master_chat_type}
              mobileCard={true}
            />
          )}
        {!is_cancelled &&
          !chat_details.is_product_demo &&
          chat_details.created_by_patient && (
            <RescheduleChatBtn
              {...chat_details}
              is_confirmed={is_confirmed}
              master_chat_type={master_chat_type}
              mobileCard={true}
            />
          )}
        {!is_cancelled && !chat_details.created_by_patient && (
          <UnconfirmedAppointmentActions
            chat_id={chat_details.chat_id}
            accepted={!is_confirmed ? null : is_confirmed}
            mobileCard={true}
          />
        )}
        {!is_cancelled && is_confirmed && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setCalendarModalOpen(true)}
            style={{
              width: "100%",
              whiteSpace: "nowrap"
            }}
            aria-label={translate("Add to Calendar")}
          >
            <CalendarTodayIcon />
            <span>
              {translate("Add to Calendar")}
            </span>
          </Button>
        )}
        {chat_details.can_be_cancelled && (
          <ConfirmationModal
            buttonText="cancel"
            title="cancelAppointment"
            message="cancelAppointmentConfirmationMessage"
            onYes={closeModal =>
              onCancelAppointment(chat_details.slot_id, closeModal)
            }
            spinner={displaySpinner}
            ariaLabelledBy={getCancelAriaLabelledBy(chat_details)}
            mobileCard={true}
          />
        )}
      </div>
      <AddToCalendar
        open={calendarModalOpen}
        closeModal={() => setCalendarModalOpen(false)}
        title={calendarTitle}
        description={calendarDescription}
        startDate={startTimeFromCSTToBrowser}
        endDate={endTimeFromCSTToBrowser}
      />
    </div>
  );
};

UpcomingChatMobileCard.propTypes = {
  chats: PropTypes.arrayOf(PropTypes.object),
  master_chat_type: PropTypes.string
};

export default UpcomingChatMobileCard;
